/**
 * AddProjectModal — Proje ekleme / düzenleme modalı (tek modal, edit'te dolu gelir).
 *
 * Alanlar: ad (zorunlu), kod, cari seçimi (opsiyonel autocomplete),
 * durum, başlangıç/bitiş tarihi, açıklama.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import {
  createProject,
  updateProject,
  type Project,
  type ProjectStatus,
} from '../../../services/projectService';
import { AppDatePicker } from '../../common/AppDatePicker';

interface AccountOption {
  id: number;
  name: string;
  code?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Başarılı kayıttan sonra çağrılır; parametre düzenleme modunda mıydı bilgisidir. */
  onSuccess: (isEdit: boolean) => void;
  /** Düzenleme modu: dolu proje verisi; ekleme modunda null. */
  editData?: Project | null;
  /** Yeni kayıtta CreatedBy olarak gönderilir (kullanıcı e-postası). */
  createdBy?: string;
}

/** "2026-07-30T00:00:00" → "2026-07-30" (date input değeri). */
const toDateInput = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.substring(0, 10);
};

const AddProjectModal: React.FC<Props> = ({ open, onClose, onSuccess, editData, createdBy }) => {
  const { t } = useTranslation('common2');
  const isEdit = Boolean(editData);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form alanları
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('active');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Cari seçimi
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);

  // Cari hesapları yükle (modal açıldığında)
  useEffect(() => {
    if (!open) return;
    const loadAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
        const accountsData = response.data?.data || response.data?.Data || response.data;
        if (Array.isArray(accountsData)) {
          setAccounts(
            accountsData.map((acc: any) => ({
              id: acc.Id || acc.id,
              name: acc.CompanyName || acc.companyName || acc.Name || acc.name || '',
              code: acc.AccountCode || acc.accountCode || acc.Code || acc.code || '',
            }))
          );
        }
      } catch (err) {
        console.error('[AddProjectModal] Cari hesaplar yüklenemedi:', err);
      } finally {
        setLoadingAccounts(false);
      }
    };
    loadAccounts();
  }, [open]);

  // Formu doldur / sıfırla
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editData) {
      setName(editData.name);
      setCode(editData.code || '');
      setStatus(editData.status);
      setDescription(editData.description || '');
      setStartDate(toDateInput(editData.startDate));
      setEndDate(toDateInput(editData.endDate));
    } else {
      setName('');
      setCode('');
      setStatus('active');
      setDescription('');
      setStartDate('');
      setEndDate('');
      setSelectedAccount(null);
    }
  }, [open, editData]);

  // Düzenlemede cari seçimini hesaplar yüklendikten sonra eşle
  useEffect(() => {
    if (!open || !editData) return;
    if (editData.accountId && accounts.length > 0) {
      setSelectedAccount(accounts.find((a) => a.id === editData.accountId) || null);
    } else if (!editData.accountId) {
      setSelectedAccount(null);
    }
  }, [open, editData, accounts]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('projects.modal.nameRequired'));
      return;
    }
    if (startDate && endDate && endDate < startDate) {
      setError(t('projects.modal.dateRangeInvalid'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const common = {
        name: trimmedName,
        code: code.trim() || null,
        status,
        accountId: selectedAccount?.id ?? null,
        description: description.trim() || null,
        startDate: startDate || null,
        endDate: endDate || null,
      };

      if (isEdit && editData) {
        await updateProject({ id: editData.id, ...common });
      } else {
        await createProject({ ...common, createdBy: createdBy || null });
      }

      onSuccess(isEdit);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? t('projects.modal.editTitle') : t('projects.modal.addTitle')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={8}>
            <TextField
              label={`${t('projects.modal.name')} *`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              disabled={saving}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={t('projects.modal.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              size="small"
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(option) => (option ? option.name || '' : '')}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={selectedAccount}
              onChange={(_, newValue) => setSelectedAccount(newValue)}
              loading={loadingAccounts}
              disabled={saving}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('projects.modal.account')}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAccounts ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={saving}>
              <InputLabel id="project-modal-status-label">{t('projects.modal.status')}</InputLabel>
              <Select
                labelId="project-modal-status-label"
                value={status}
                label={t('projects.modal.status')}
                onChange={(e) => setStatus(e.target.value as ProjectStatus)}
              >
                <MenuItem value="active">{t('projects.statusActive')}</MenuItem>
                <MenuItem value="completed">{t('projects.statusCompleted')}</MenuItem>
                <MenuItem value="cancelled">{t('projects.statusCancelled')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <AppDatePicker
              label={t('projects.modal.startDate')}
              value={startDate}
              onChange={setStartDate}
              fullWidth
              size="small"
              disabled={saving}
            />
          </Grid>
          <Grid item xs={6}>
            <AppDatePicker
              label={t('projects.modal.endDate')}
              value={endDate}
              onChange={setEndDate}
              fullWidth
              size="small"
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('projects.modal.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
              disabled={saving}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('projects.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {t('projects.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProjectModal;
